import { useMemo, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { Label } from '@rossum/api-client/labels';
import { QueueAction } from '@rossum/api-client/schemas';
import {
  IconCustomTagQuestion,
  IconPlus,
  IconTag,
  IconTagOff,
} from '@rossum/ui/icons/tabler';
import {
  Autocomplete,
  Button,
  Dialog,
  Stack,
  SvgIcon,
  TextField,
} from '@rossum/ui/material';
import { getUnsafe } from '../../../lib/helpers';
import { CreateLabelDialog } from '../../labels/components/LabelDialogs';
import { useRequestUnpaginatedLabels } from '../../labels/hooks/useRequestLabels';
import { createForm } from './createForm';
import { createHeader } from './createHeader';

const MAX_ENTRIES = 3;

const labelActionPayloadSchema = (intl?: IntlShape) =>
  z.object({
    labels: z
      .array(z.string())
      .optional()
      .refine(
        val => val && val.length > 0,
        intl?.formatMessage({
          id: 'features.queueSettings.rules.actions.label.payload.labelId.required',
        })
      ),
  });

const getSelectedLabels = (
  value: z.infer<ReturnType<typeof labelActionPayloadSchema>>,
  data: Label[] | undefined
) => {
  const { labels } = value;
  if (data && labels?.length) {
    return data.filter(label => labels.includes(label.url));
  }

  return [];
};

const LabelActionForm = createForm(
  ({ value, onChange, errors }) => {
    const intl = useIntl();
    const { isSuccess, data } = useRequestUnpaginatedLabels();
    const [showCreateLabelDialog, setShowCreateLabelDialog] = useState(false);

    const stableValues = useMemo(() => {
      return getSelectedLabels(value, data);
    }, [data, value]);

    return isSuccess ? (
      <Stack spacing={1}>
        <Autocomplete
          multiple
          size="small"
          options={data}
          getOptionLabel={option => `${option.name}`}
          value={stableValues}
          onChange={(_e, vs) => {
            onChange({
              ...value,
              labels: vs.map(({ url }) => url),
            });
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={intl.formatMessage({
                id: 'features.queueSettings.rules.actions.label.form.label',
              })}
              placeholder={intl.formatMessage({
                id: 'features.queueSettings.rules.actions.label.form.labelPlaceholder',
              })}
              InputLabelProps={{ shrink: true }}
              error={getUnsafe(errors, 'labels') !== undefined}
              helperText={getUnsafe(errors, 'labels.message')?.toString()}
            />
          )}
        />
        <Button
          size="small"
          variant="text"
          onClick={() => setShowCreateLabelDialog(true)}
          startIcon={
            <SvgIcon fontSize="small">
              <IconPlus />
            </SvgIcon>
          }
          sx={{ alignSelf: 'flex-end' }}
        >
          {intl.formatMessage({
            id: 'features.queueSettings.rules.actions.label.form.createLabel',
          })}
        </Button>
        <Dialog open={showCreateLabelDialog} PaperProps={{ elevation: 2 }}>
          <CreateLabelDialog
            onClose={() => setShowCreateLabelDialog(false)}
            onSuccess={newLabel => {
              onChange({
                ...value,
                labels: [...(value.labels ?? []), newLabel.url],
              });
            }}
          />
        </Dialog>
      </Stack>
    ) : null;
  },
  (intl?: IntlShape) => labelActionPayloadSchema(intl)
);

const LabelActionHeader = createHeader(
  ({ value, RenderComponent }) => {
    const intl = useIntl();
    const { data } = useRequestUnpaginatedLabels();

    const stableValues = useMemo(() => {
      return getSelectedLabels(value, data);
    }, [data, value]);

    return (
      <RenderComponent
        entries={[
          ...stableValues.slice(0, MAX_ENTRIES).flatMap(stableValue => [
            {
              key: `label_${stableValue.id}`,
              label: intl.formatMessage({
                id: 'features.queueSettings.rules.actions.label.form.label',
              }),
              value: `${stableValue.name}`,
            },
          ]),
          ...(stableValues.length > MAX_ENTRIES
            ? [
                {
                  key: 'more_labels',
                  value: `+${stableValues.length - MAX_ENTRIES}`,
                },
              ]
            : []),
        ]}
      />
    );
  },
  (intl?: IntlShape) => labelActionPayloadSchema(intl)
);

export const addLabelAction = {
  icon: IconTag,
  intlKey: 'addLabel' as const,
  resolveAction: (a: QueueAction) => a.type === 'add_label',
  serialized: { type: 'add_label' as const },
  groupIntlKey: 'labels' as const,
  form: LabelActionForm,
  header: LabelActionHeader,
  supportedEvents: [
    'validation',
    'annotation_import_succeeded',
    'annotation_import_failed',
    'annotation_export_succeeded',
    'annotation_export_failed',
  ] as const,
  payloadSchema: (intl?: IntlShape) => labelActionPayloadSchema(intl),
};

export const removeLabelAction = {
  icon: IconTagOff,
  intlKey: 'removeLabel' as const,
  resolveAction: (a: QueueAction) => a.type === 'remove_label',
  serialized: { type: 'remove_label' as const },
  groupIntlKey: 'labels' as const,
  form: LabelActionForm,
  header: LabelActionHeader,
  supportedEvents: [
    'validation',
    'annotation_import_succeeded',
    'annotation_import_failed',
    'annotation_export_succeeded',
    'annotation_export_failed',
  ] as const,
  payloadSchema: (intl?: IntlShape) => labelActionPayloadSchema(intl),
};

export const addRemoveLabelAction = {
  icon: IconCustomTagQuestion,
  intlKey: 'addRemoveLabel' as const,
  resolveAction: (a: QueueAction) => a.type === 'add_remove_label',
  serialized: { type: 'add_remove_label' as const },
  groupIntlKey: 'labels' as const,
  form: LabelActionForm,
  header: LabelActionHeader,
  supportedEvents: [
    'validation',
    'annotation_import_succeeded',
    'annotation_import_failed',
    'annotation_export_succeeded',
    'annotation_export_failed',
  ] as const,
  payloadSchema: (intl?: IntlShape) => labelActionPayloadSchema(intl),
};

export const labelActions = [
  addRemoveLabelAction,
  addLabelAction,
  removeLabelAction,
];
